<script setup lang="ts">
import { useRefHistory } from "@vueuse/core";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref, watch } from "vue";
import { UserInfoDto } from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { splitByCaps } from "~/utils/miscUtils";

const showDebugInfo = false;

const props = defineProps<{
  drawerOpen: boolean;
  addingNewUser: boolean;
}>();

const emit = defineEmits<{
  (e: "updated"): void;
  (e: "drawerClosed"): void;
}>();

const myInfoStore = useMyInfoStore();

const keySendEmail = ref(0);
const user = ref<UserInfoDto>({ ...myInfoStore.userInfo });
const addingNewUser = ref(props.addingNewUser);
const isTempAccount = computed(() => myInfoStore.userInfo.isTempAccount);
const showSendEmail = ref(!addingNewUser.value && isTempAccount.value);

/** edit history for the user */
const editHistory = useRefHistory(user, { deep: true });

type issueType = {
  d: string;
  o?: string | null;
  n?: string | null;
};

const changesToSave = computed(() => {
  const issues: issueType[] = [];
  const originalUser = editHistory.history.value.at(-1)?.snapshot;
  if (originalUser) {
    if (originalUser.defaultSysOrgGuid !== user.value.defaultSysOrgGuid) {
      issues.push({ d: "Starting SysOrg changed" });
    }
  }
  return issues;
});

const showDefaultOrg = computed(() => myInfoStore.hasMultipleOrgs);

const saveNeeded = computed(() => changesToSave.value.length > 0);

const handleClose = async () => {
  if (saveNeeded.value) {
    ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    })
      // If the user confirms
      .then(() => {
        // do nothing
      })
      // If the user cancels
      .catch(() => {
        cancelChanges();
        emit("drawerClosed");
      });
  } else {
    emit("drawerClosed");
  }
};

const cancelChanges = () => {
  const originalUser = editHistory.history.value.at(-1)?.snapshot;
  if (originalUser) {
    user.value.loginName = originalUser.loginName;
    user.value.email = originalUser.email;
    user.value.defaultSysOrgGuid = originalUser.defaultSysOrgGuid;
  }
};

const setNewToOriginal = () => {
  editHistory.clear();
};

const saveChanges = async () => {
  if (!user.value.defaultSysOrgGuid) {
    return;
  }
  // user.value.loginName = myInfoStore.userInfo;
  // user.value.currentOrgGuid = myInfoStore.currentOrg.guid;
  // user.value.currentOrgRoles = myInfoStore.currentOrg.roles;
  // user.value.userGuid = myInfoStore.userInfo?.userGuid;
  // user.value.userOrgNames =
  //   myInfoStore.userInfo.organizations?.map((org) => org.name) ?? [];
  // user.value.email = myInfoStore.userInfo.email;

  await apiClient.userInfo
    .setStartingOrg(user.value.defaultSysOrgGuid)
    .then((success) => {
      if (success) {
        emit("updated");
        ElMessage.success("Saved");
        emit("drawerClosed");
      }
    })
    .catch(showError);

  return;
};

const handleUpdateOrg = (newOrg: string) => {
  if (newOrg) {
    user.value.defaultSysOrgGuid = newOrg;
  }
};

const onSave = async () => {
  await saveChanges();
  setNewToOriginal(); // this is to disable the save button again
};

watch(
  () => showSendEmail,
  () => {
    keySendEmail.value++;
  },
);

onMounted(async () => {
  await Promise.all([myInfoStore.load()]);

  if (!user.value?.defaultSysOrgGuid) {
    return;
  }
  // sessionStorage.setItem("currentOrgGuid", user.value?.defaultSysOrgGuid);
});

const hasOnlyGuestRole = computed(() => {
  const roles = myInfoStore.currentSysOrg?.roles;
  return roles?.length === 1 && roles[0] === "Guest";
});

const toggle = computed(() => {
  return saveNeeded.value ? "Cancel" : "Close";
});
</script>
<template>
  <el-drawer
    v-if="user"
    :model-value="drawerOpen"
    :title="'Site Preferences'"
    :before-close="handleClose"
    :destroy-on-close="true"
    direction="rtl"
    size="80%"
    class="user-drawer"
  >
    <hr />
    <div class="user-info">
      <el-form :model="user" label-width="auto">
        <el-form-item label="Name">
          <div>{{ user.loginName }}</div>
        </el-form-item>
        <el-form-item label="Email">
          <div>{{ user.email }}</div>
          <span v-if="isTempAccount" class="tempAccount"
            >This person has not signed in yet.</span
          >
        </el-form-item>
        <el-form-item
          v-if="!hasOnlyGuestRole"
          label="Roles in this Organization"
          class="roles"
        >
          {{
            myInfoStore.currentSysOrg?.roles
              .map((s) => splitByCaps(s))
              .join(", ")
          }}
        </el-form-item>

        <el-form-item v-if="showDefaultOrg" label="Starting System Organization"
          ><el-select
            v-model="user.defaultSysOrgGuid"
            style="width: 240px"
            :disabled="!myInfoStore.hasMultipleOrgs"
            placeholder="Select organization..."
            filterable
            @update:model-value="handleUpdateOrg"
          >
            <el-option
              v-for="org in myInfoStore.mySysOrgs"
              :key="org.guid"
              :label="splitByCaps(org.name)"
              :value="org.guid || ''"
            />
          </el-select>
          <span class="comment"
            >Which organization would you like to see after logging in?</span
          >
        </el-form-item>

        <el-form-item style="margin-top: 20px" label="&nbsp;">
          <el-button type="primary" :disabled="!saveNeeded" @click="onSave"
            >Save</el-button
          >
          <el-button
            :type="toggle === 'Cancel' ? 'danger' : ''"
            :plain="toggle === 'Cancel'"
            @click="handleClose"
            >{{ toggle }}</el-button
          >
        </el-form-item>
        <el-form-item
          v-if="showDebugInfo && changesToSave.length"
          label="To Save"
        >
          <ul class="unsavedChanges">
            <li v-for="issue in changesToSave" :key="issue">
              {{ issue.d ?? issue }}
            </li>
          </ul>
        </el-form-item>
      </el-form>

      <div class="loginPasswords">
        Login passwords are changed where you logged in.
      </div>
    </div>
  </el-drawer>
</template>
<style lang="less">
.user-drawer {
  display: flex;
  justify-content: space-between;
  padding-bottom: 1rem;

  .el-drawer__header {
    margin-bottom: 1rem;

    .el-drawer__title {
      padding-left: 0.8rem;
      font-size: 2rem;
      color: var(--el-color-black);
    }
  }

  .el-drawer__body {
    padding: 0 4rem 1rem 2rem;

    .user-info {
      .el-table {
        margin-top: 1em;
      }
    }
  }

  .tempAccount {
    font-size: 0.9rem;
    margin-left: 1em;
    color: var(--el-color-primary-light-3);
  }
  .comment {
    font-size: 0.9rem;
    margin: 0 0 0 1em;
    color: var(--el-color-primary-light-5);
  }
  .guestSwitch {
    --el-switch-off-color: var(--el-color-success);
    --el-switch-on-color: var(--el-color-warning);
  }
  .unsavedChanges {
    margin: 0;
    font-size: inherit;
  }
  .superAdmin {
    height: fit-content;
    padding: 0.5em 1em 0.8em;
    font-size: 0.75em;
    border: solid 2px var(--el-color-danger-light-3);
    border-radius: 7px;
    background-color: var(--el-color-danger-light-8);

    .el-form-item__label {
      font-size: 1.15em;
      color: var(--el-color-danger);
    }

    &.notSelected {
      border: solid 2px var(--el-color-info-light-3);
      background-color: var(--el-color-info-light-8);
      opacity: 0.5;
      &:hover {
        opacity: 1;
        border-color: var(--el-color-danger-light-3);
      }

      .el-form-item__label {
        color: var(--el-color-info);
      }
    }
  }
  .loginPasswords {
    margin-top: 2em;
    font-size: 0.9rem;
    color: var(--el-color-primary-light-5);
  }
}
</style>
